import { useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Champ, ChampPuces } from "@/composants/ui/champ";
import { horodatage } from "@/lib/libelles";
import type { CanalSignature, Fiche, QualiteSignataire, SignatureFiche } from "@/lib/api";

/**
 * Les signatures d'une fiche (ticket #112, ADR-0004, ADR-0066) : celles que son type
 * attend, celles qui sont posées et celles qui manquent, en toutes lettres (ADR-0024).
 *
 * Qui présente l'écran fait signer au doigt ou au stylet : le tracé part en PNG, avec le
 * nom et la qualité du signataire. Le serveur garde qui a présenté l'écran, l'heure et le
 * contexte technique ; il refuse une fiche incomplète, et la base une qualité que la fiche
 * n'attend pas. La première signature fige la fiche ; la dernière scelle son PDF.
 */

export const QUALITES_SIGNATAIRE: Record<QualiteSignataire, string> = {
  CLIENT: "client",
  COLLABORATEUR: "collaborateur",
  INTERVENANT: "intervenant",
};

const CANAUX: Record<CanalSignature, string> = {
  SESSION_INTERNE: "en session interne",
  ESPACE_CLIENT: "depuis l'espace client",
  LIEN_COURRIEL: "par un lien reçu par courriel",
};

export type SignatureSaisie = {
  signataire_nom: string;
  signataire_type: QualiteSignataire;
  trace_png: string;
};

function provenance(signature: SignatureFiche) {
  const canal = CANAUX[signature.canal];
  return signature.presentee_par ? `${canal}, écran présenté par ${signature.presentee_par}` : canal;
}

export function BlocSignatures({
  titre,
  fiche,
  onSigner,
}: {
  titre: string;
  fiche: Fiche;
  /** Rend `null` si le serveur a refusé : la saisie reste à l'écran. */
  onSigner: (signature: SignatureSaisie) => Promise<unknown>;
}) {
  const qualites = [...new Set(fiche.signatures_manquantes)];
  return (
    <section className="flex flex-col gap-3 sm:col-span-2" aria-label={titre}>
      <h3 className="text-sm font-medium text-encre">{titre}</h3>
      {fiche.signatures.length || fiche.signatures_manquantes.length ? (
        <ul className="flex flex-col divide-y divide-filet rounded-[6px] border border-filet">
          {fiche.signatures.map((signature) => (
            <li key={signature.id} className="p-2 text-sm">
              <p className="break-words text-encre">
                Signée par {signature.signataire_nom},{" "}
                {QUALITES_SIGNATAIRE[signature.signataire_type]}
              </p>
              <p className="text-xs text-encre-douce">
                Le {horodatage(signature.signe_le)}, {provenance(signature)}
              </p>
            </li>
          ))}
          {fiche.signatures_manquantes.map((qualite, rang) => (
            <li key={`${qualite}-${rang}`} className="p-2 text-sm text-encre-douce">
              Signature attendue : {QUALITES_SIGNATAIRE[qualite]}, pas encore posée
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-encre-douce">Le type de cette fiche n'attend aucune signature.</p>
      )}
      {fiche.signable ? (
        <FormulaireSignature
          // Une signature posée vide le formulaire pour la suivante.
          key={fiche.signatures.length}
          qualites={qualites}
          brouillon={fiche.statut === "BROUILLON"}
          onSigner={onSigner}
        />
      ) : null}
    </section>
  );
}

function FormulaireSignature({
  qualites,
  brouillon,
  onSigner,
}: {
  qualites: QualiteSignataire[];
  brouillon: boolean;
  onSigner: (signature: SignatureSaisie) => Promise<unknown>;
}) {
  const [qualite, setQualite] = useState<QualiteSignataire>(qualites[0]);
  const [nom, setNom] = useState("");
  const [trace, setTrace] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [manque, setManque] = useState<string | null>(null);

  async function signer() {
    if (!nom.trim() || !trace) {
      setManque(
        !nom.trim()
          ? "Le nom du signataire est obligatoire."
          : "Le signataire trace sa signature dans le cadre.",
      );
      return;
    }
    setManque(null);
    setEnvoi(true);
    await onSigner({ signataire_nom: nom, signataire_type: qualite, trace_png: trace });
    setEnvoi(false);
  }

  return (
    <div className="flex flex-col gap-3 rounded-[6px] border border-filet p-3">
      {brouillon ? (
        <p className="text-sm text-encre-douce">
          La première signature fige la fiche : ce qui est à l'écran est ce qui se signe.
        </p>
      ) : null}
      {qualites.length > 1 ? (
        <ChampPuces
          libelle="Qualité du signataire"
          required
          valeur={qualite}
          onChanger={(choix) => setQualite(choix as QualiteSignataire)}
          options={qualites.map((q) => ({ valeur: q, libelle: QUALITES_SIGNATAIRE[q] }))}
        />
      ) : (
        <p className="text-sm text-encre">Signature attendue : {QUALITES_SIGNATAIRE[qualite]}</p>
      )}
      <Champ
        libelle="Nom du signataire"
        required
        maxLength={200}
        autoComplete="off"
        value={nom}
        onChange={(e) => setNom(e.target.value)}
      />
      <PaveSignature onTrace={setTrace} />
      {manque ? (
        <p className="text-sm text-alerte" role="alert">
          {manque}
        </p>
      ) : null}
      <div>
        <Bouton type="button" variante="principal" disabled={envoi} onClick={() => void signer()}>
          <PenLine className="h-4 w-4" aria-hidden />
          Enregistrer la signature
        </Bouton>
      </div>
    </div>
  );
}

// La résolution du tracé ; le cadre s'affiche à la largeur de l'écran.
const LARGEUR = 600;
const HAUTEUR = 200;

/** Le cadre où l'on signe au doigt, au stylet ou à la souris. */
function PaveSignature({ onTrace }: { onTrace: (png: string | null) => void }) {
  const cadre = useRef<HTMLCanvasElement>(null);
  const dernier = useRef<{ x: number; y: number } | null>(null);
  const trace = useRef(false);
  const [vide, setVide] = useState(true);

  function point(evenement: React.PointerEvent<HTMLCanvasElement>) {
    const rect = evenement.currentTarget.getBoundingClientRect();
    return {
      x: ((evenement.clientX - rect.left) / rect.width) * LARGEUR,
      y: ((evenement.clientY - rect.top) / rect.height) * HAUTEUR,
    };
  }

  function tracer(evenement: React.PointerEvent<HTMLCanvasElement>) {
    const contexte = cadre.current?.getContext("2d");
    if (!contexte || !dernier.current) return;
    const suivant = point(evenement);
    contexte.lineWidth = 3;
    contexte.lineCap = "round";
    contexte.strokeStyle = "#000";
    contexte.beginPath();
    contexte.moveTo(dernier.current.x, dernier.current.y);
    contexte.lineTo(suivant.x, suivant.y);
    contexte.stroke();
    dernier.current = suivant;
    trace.current = true;
    setVide(false);
  }

  function lever() {
    if (!dernier.current) return;
    dernier.current = null;
    const png = cadre.current?.toDataURL("image/png") ?? "";
    onTrace(trace.current ? png.replace(/^data:image\/png;base64,/, "") : null);
  }

  function effacer() {
    cadre.current?.getContext("2d")?.clearRect(0, 0, LARGEUR, HAUTEUR);
    trace.current = false;
    setVide(true);
    onTrace(null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm font-medium text-encre" id="pave-signature">
        Tracé de la signature
        <span aria-hidden className="ml-0.5 text-alerte">
          *
        </span>
      </p>
      <canvas
        ref={cadre}
        width={LARGEUR}
        height={HAUTEUR}
        aria-labelledby="pave-signature"
        role="img"
        className="aspect-[3/1] w-full max-w-xl touch-none rounded-[6px] border border-filet bg-white"
        onPointerDown={(evenement) => {
          evenement.currentTarget.setPointerCapture(evenement.pointerId);
          dernier.current = point(evenement);
        }}
        onPointerMove={tracer}
        onPointerUp={lever}
        onPointerCancel={lever}
      />
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-encre-douce">
          {vide ? "Le cadre est vide : signez au doigt ou au stylet." : "Signature tracée."}
        </p>
        <Bouton type="button" variante="discret" onClick={effacer} disabled={vide}>
          <Eraser className="h-4 w-4" aria-hidden />
          Effacer le tracé
        </Bouton>
      </div>
    </div>
  );
}
